import { Coordinate } from 'ol/coordinate';
import { alertWarn, alertSuccess } from '../../alertDispatcher';
import { Mission, Zone } from '../../db';
import { dispatchMissionUpdated } from '../../missionEvents';
import { dispatchCoresNeedUpdate, dispatchZonesUpdated } from '../../mapEvents';
import holesMerger from './HolesMerger';
import polysMerger from './PolysMerger';
import { Geometry, SimpleGeometry } from 'ol/geom';
import Feature from 'ol/Feature';
import { polygonCutter } from './polygon-cutter/PolygonCutter';
import { MultiPolygonCoords, Poly } from '../../types/types';
import PolyManager from './PolyManager';

class ZonesManager {
  addMultipolygonToZone(multiPolygonCoords: MultiPolygonCoords, zone: Zone): { polys: Poly[]; errors?: string[] } {
    const polys: Poly[] = [];
    const errorMessages: string[] = [];

    const mission = zone.getMission();

    if (!mission) {
      errorMessages.push('Zone has no associated mission');

      return { polys: [], errors: errorMessages };
    }

    const polyManager = new PolyManager(mission, zone);

    for (const polygonCoords of multiPolygonCoords) {
      const { poly, errors } = polyManager.createPoly(polygonCoords);

      if (errors) {
        errorMessages.push(...errors);
      }

      if (poly) {
        polys.push(poly);
      }
    }

    return { polys, errors: errorMessages.length > 0 ? errorMessages : undefined };
  }

  async cutPoly(mission: Mission, feature: Feature<Geometry>, originalZoneId: number, cutterCoordinates: Coordinate[]) {
    if (cutterCoordinates.length < 2) {
      alertWarn('Cutting line must have at least 2 points');

      return;
    }

    const geometry: Geometry | undefined = feature.getGeometry();

    if (!geometry) {
      alertWarn('Feature has no geometry');

      return;
    }

    const { shape: resultingMultipolygon, error } = polygonCutter.cutPolygon(
      geometry as SimpleGeometry,
      cutterCoordinates,
    );

    if (error || !resultingMultipolygon) {
      alertWarn(`Failed to cut polygon: ${error}`);

      return;
    }

    const zone = mission.getZoneByPolyId(originalZoneId);

    if (!zone) {
      alertWarn('Original zone not found in mission');

      return;
    }

    const originalPoly = zone.getPolysGroupedByPolyId()[originalZoneId];

    if (!originalPoly) {
      alertWarn('Original poly not found in zone');

      return;
    }

    // First, add new polygons as boundaries to the original zone.
    // If we delete the original boundary first and if it's the only one,
    // the zone gets deleted as well.
    const { polys, errors } = this.addMultipolygonToZone(resultingMultipolygon, zone);

    if (errors) {
      alertWarn(`Some errors occurred while adding new polygons: \n- ${errors.join('\n- ')}`);
    }

    if (polys.length > 0) {
      alertSuccess(
        `Polygon cut successfully into ${polys.length} new polygon(s): ${polys.map((p) => p.polyId).join(', ')}`,
      );

      // Delete the original poly
      zone.disposePolyById(originalPoly.polyId);
    }

    dispatchMissionUpdated();
    // Polygon cut may dispose the original polygon's boundaries,
    // which can orphan the containing zone's sample → cores.
    dispatchCoresNeedUpdate();
    dispatchZonesUpdated();
  }

  mergePolygonHoles(mission: Mission, firstHoleId: number, secondHoleId: number) {
    const firstHole = mission.getInnerBoundaryById(firstHoleId);
    const secondHole = mission.getInnerBoundaryById(secondHoleId);

    if (!firstHole || !secondHole) {
      alertWarn(`One of the holes not found, aborting.`);

      return;
    }

    if (firstHole.poly_id !== secondHole.poly_id) {
      alertWarn(`Holes belong to different zones, aborting.`);

      return;
    }

    const firstZone = firstHole.getInnerZone();
    const secondZone = secondHole.getInnerZone();

    if (!firstZone || !secondZone || firstZone.instance_id !== secondZone.instance_id) {
      alertWarn(`Inner zones belong to different boundaries, aborting.`);

      return;
    }

    const { coordinates, error } = holesMerger.merge(firstHole.toGeometry(), secondHole.toGeometry());

    if (error) {
      alertWarn(error);

      return;
    }

    // Update first hole with new coordinates
    firstHole.coordinates = coordinates.map((c) => c.slice().reverse());

    // Remove second hole
    mission.removeInnerBoundary(secondHoleId);

    alertSuccess(`Holes merged successfully.`);

    dispatchMissionUpdated();
  }

  mergeZonePolys(mission: Mission, firstPolyId: number, secondPolyId: number) {
    const zone = mission.getZoneByPolyId(firstPolyId);

    if (!zone) {
      alertWarn(`Zone for poly ${firstPolyId} not found, aborting.`);

      return;
    }

    const secondZone = mission.getZoneByPolyId(secondPolyId);

    if (!secondZone) {
      alertWarn(`Zone for poly ${secondPolyId} not found, aborting.`);

      return;
    }

    if (zone.instance_id !== secondZone.instance_id) {
      alertWarn(`Polygons belong to different zones, aborting.`);

      return;
    }

    const polys = zone.getPolysGroupedByPolyId();
    const firstPoly = polys[firstPolyId];
    const secondPoly = polys[secondPolyId];

    if (!firstPoly || !secondPoly) {
      alertWarn(`One or both polygons not found in zone, aborting.`);

      return;
    }

    // Convert polys to GeoJSON for merging
    const firstPolyGeometry = firstPoly.outerBoundary.toGeometry();

    // Add holes to first poly if they exist
    if (firstPoly.innerBoundaries.length > 0) {
      firstPolyGeometry.geometry.coordinates.push(
        ...firstPoly.innerBoundaries.map((ib) => ib.toGeometry().geometry.coordinates[0]),
      );
    }

    const secondPolyGeometry = secondPoly.outerBoundary.toGeometry();

    // Add holes to second poly if they exist
    if (secondPoly.innerBoundaries.length > 0) {
      secondPolyGeometry.geometry.coordinates.push(
        ...secondPoly.innerBoundaries.map((ib) => ib.toGeometry().geometry.coordinates[0]),
      );
    }

    const { multiPolygonCoords, error } = polysMerger.merge(firstPolyGeometry, secondPolyGeometry);

    if (error) {
      alertWarn(error);

      return;
    }

    // Add the merged polygon(s) back to the zone
    const { polys: newPolys, errors } = this.addMultipolygonToZone(multiPolygonCoords, zone);

    if (errors) {
      alertWarn(`Some errors occurred while adding merged polygon: \n- ${errors.join('\n- ')}`);
    }

    if (newPolys.length > 0) {
      // Remove both original polys
      zone.disposePolyById(firstPolyId);
      zone.disposePolyById(secondPolyId);

      alertSuccess(
        `Polygons merged successfully into ${newPolys.length} new polygon(s): ${newPolys.map((p) => p.polyId).join(', ')}`,
      );
    }

    dispatchMissionUpdated();
    // Merging polygons disposes the original polys; the containing
    // zone's sample → cores may be orphaned.
    dispatchCoresNeedUpdate();
    dispatchZonesUpdated();
  }
}

const zonesManager = new ZonesManager();

export default zonesManager;
